import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { DiscountBadge } from '@/components/DiscountBadge';
import { useLanguage } from '@/contexts/LanguageContext';

interface PriceDisplayProps {
  price: number;
  originalPrice?: number;
  discountPercentage?: number;
  className?: string;
  showLoginPrompt?: boolean;
  showPriceToUnregistered?: boolean;
  hidePriceWithoutConsultation?: boolean;
  requiresConsultation?: boolean;
  hasConsultationApproval?: boolean;
}

export const PriceDisplay: React.FC<PriceDisplayProps> = ({ 
  price, 
  originalPrice, 
  discountPercentage = 0,
  className = "",
  showLoginPrompt = true,
  showPriceToUnregistered = false,
  hidePriceWithoutConsultation = false,
  requiresConsultation = false,
  hasConsultationApproval = false
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Handle pricing logic correctly - price field contains original price, discount_percentage is used to calculate current price
  const hasDiscount = discountPercentage > 0;
  
  let actualCurrentPrice = price; // Default to price field
  let actualOriginalPrice = originalPrice || 0; // Use originalPrice prop if provided
  
  if (hasDiscount) {
    // When there's a discount, price field contains the original price
    actualOriginalPrice = originalPrice || price; // Use originalPrice prop, or fall back to price field
    actualCurrentPrice = price * (1 - discountPercentage / 100); // Calculate discounted price
  }

  // Check if price should be hidden due to consultation requirements
  if (hidePriceWithoutConsultation && requiresConsultation && !hasConsultationApproval) {
    return (
      <div className={`text-muted-foreground ${className}`}>
        {t.priceDisplay.priceVisibleAfterConsultation}
      </div>
    );
  }

  // If the product allows showing price to unregistered users, or if user is logged in, show the price
  if (showPriceToUnregistered || user) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="text-primary font-semibold text-lg">
              €{actualCurrentPrice.toFixed(2)}
            </span>
            {discountPercentage > 0 && (
              <DiscountBadge 
                discountPercentage={discountPercentage}
                size="sm"
              />
            )}
          </div>
          {(actualOriginalPrice > actualCurrentPrice && actualCurrentPrice > 0) && (
            <span className="text-sm text-muted-foreground line-through">
              €{actualOriginalPrice.toFixed(2)}
            </span>
          )}
        </div>
      </div>
    );
  }

  // If user is not logged in and product doesn't allow showing price to unregistered users
  if (!showLoginPrompt) {
    return (
      <div className={`text-muted-foreground ${className}`}>
        {t.priceDisplay.loginToSeePrices}
      </div>
    );
  }
  
  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <div className="text-muted-foreground text-sm">
        {t.priceDisplay.loginToSeePrices}
      </div>
      <Button 
        size="sm" 
        variant="outline"
        onClick={() => navigate('/auth')}
        className="w-fit"
      >
        {t.priceDisplay.login}
      </Button>
    </div>
  );
};